<template>
    <psi-dialog
        heading-color="accent darken-1"
        title="Add Students"
        scrollable
        icon="mdi-account-multiple-plus"
        @close="$emit('close')"
        @save="onSave"
    >
        <fill-mock-names
            v-if="shouldMock"
            @fill-names="fillNames"
            :count="studentCount"
        ></fill-mock-names>
        <v-alert prominent outlined color="accent" class="mb-5">
            <p v-if="addType === 'with-email'">
                Enter student name(s) and email(s) and we will send an
                invitation to complete their report.
            </p>
            <p v-else>
                Enter student name(s). We will generate a registration code that
                can be used by you on behalf of the student or by the student.
            </p>
        </v-alert>

        <psi-form-section
            two-cols
            :value="data"
            name="student-data"
            :fields="studentFields"
            color="accent"
        ></psi-form-section>
        <div class="d-flex">
            <v-tooltip top color="warning darken-2">
                <template v-slot:activator="{ attr, on }">
                    <v-btn
                        fab
                        color="warning darken-2"
                        v-on="on"
                        v-bind="attr"
                        @click.stop="removeFields"
                        :disabled="studentCount === 1"
                        class="mr-3"
                    >
                        <v-icon>mdi-minus</v-icon>
                    </v-btn>
                </template>
                <span>Remove Student</span>
            </v-tooltip>
            <v-tooltip top color="accent">
                <template v-slot:activator="{ attr, on }">
                    <v-btn
                        fab
                        color="accent"
                        v-on="on"
                        v-bind="attr"
                        @click.stop="addFields(1)"
                    >
                        <v-icon>mdi-plus</v-icon>
                    </v-btn>
                </template>
                <span>Add Additional Students</span>
            </v-tooltip>
        </div>
        <v-snackbar v-model="snackbar" centered color="white">
            <v-card color="grey lighten-3">
                <v-card-title class="d-flex">
                    <span class="error--text text--darken-2">Form Errors</span>
                    <v-spacer></v-spacer>
                    <v-btn small text @click.stop="snackbar = false"
                        ><v-icon small class="grey--text text--darken-5"
                            >mdi-close</v-icon
                        ></v-btn
                    >
                    <v-card-text class="error--text text--darken-2">
                        {{ error }}
                    </v-card-text>
                </v-card-title>
            </v-card>
        </v-snackbar>
    </psi-dialog>
</template>
<script>
import Mocking from "@/mixins/mocking.js";
import toPairs from "lodash.topairs";

export default {
    name: "student-add",
    mixins: [Mocking],
    components: {
        FillMockNames: () => import("./FillMockNames"),
    },
    props: {
        addType: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            fields: [],
            data: {},
            count: this.$config("program.initialStudentCount"),
            snackbar: false,
            error: "",
        };
    },
    computed: {
        field() {
            return this.addType === "with-email"
                ? {
                      type: "text",
                      name: "email",
                      icon: this.$config("icons.email"),
                      label: "Email",
                      format: "email",
                  }
                : {
                      type: "text",
                      name: "code",
                      icon: this.$config("icons.code"),
                      label: "Registration Code",
                      "persistent-hint": true,
                      hint: "We will provide you access to this code per student",
                      disabled: true,
                  };
        },
        studentFields() {
            return this.groupedFields(this.fields);
        },
        studentCount() {
            return this.studentFields.length;
        },
    },
    mounted() {
        this.addFields(this.count);
    },
    methods: {
        fillNames(students) {
            let fieldPairs = [];
            students.forEach(({ name, email }, index) => {
                const value = this.field.name === "code" ? this.code() : email;
                fieldPairs.push([`${this.field.name}-${index + 1}`, value]);
                fieldPairs.push([`name-${index + 1}`, name]);
            });
            this.data = Object.assign({}, Object.fromEntries(fieldPairs));
        },
        validField(field) {
            return toPairs(this.data).some((elem) => {
                return elem[0].split("-").shift() === field && elem[1];
            });
        },
        onSave() {
            this.error = "";
            if (this.field.name === "code") {
                if (!this.validField("name")) {
                    this.error = "Please enter at least one name";
                    this.snackbar = true;
                }
            } else {
                Object.keys(this.data).forEach((key) => {
                    if (
                        // Email field?
                        key.split("-").shift() === this.field.name &&
                        // Non-empty name w/ email field
                        this.data[key.replace(this.field.name, "name")]
                            .length == 0 &&
                        this.data[key].length > 0
                    ) {
                        this.error = `Please provide a name for each valid ${this.field.name}`;
                        this.snackbar = true;
                        return false;
                    }
                });
            }
            if (this.error.length === 0) {
                let students = [];
                Object.keys(this.data).forEach((key) => {
                    if (
                        key.split("-").shift() === this.field.name &&
                        this.data[key].length !== 0 &&
                        this.data[key.replace(this.field.name, "name")]
                            .length !== 0
                    ) {
                        students.push({
                            name: this.data[
                                key.replace(this.field.name, "name")
                            ],
                            [this.field.name]: this.data[key],
                        });
                    }
                });
                this.$emit("save", students);
            }
        },
        removeFields() {
            let data = Object.assign({}, this.data);
            delete data[`name-${this.studentCount}`];
            delete data[`${this.field.name}-${this.studentCount}`];
            this.fields.splice(this.fields.length - 2, 2);
            this.data = Object.assign({}, data);
        },
        addFields(count) {
            const index = this.studentFields.length + 1;
            let fields = [];
            let data = {};

            for (let i = 0; i < count; i++) {
                const nameKey = `name-${index + i}`;
                const idKey = `${this.field.name}-${index + i}`;
                fields.push(
                    {
                        type: "text",
                        name: nameKey,
                        key: nameKey,
                        label: "Name",
                        icon: this.$config("icons.name"),
                    },
                    {
                        ...this.field,
                        name: idKey,
                        key: idKey,
                    }
                );
                data[nameKey] = "";
                data[idKey] = this.field.name === "code" ? this.code() : "";
            }
            this.fields = this.fields.concat(fields);
            this.data = Object.assign(this.data, data);
        },
        groupedFields(fields) {
            // create a group of 2 column meta fields
            let fieldGroup = [];
            for (let i = 0; i < fields.length; i += 2) {
                let items = [];
                items.push(fields[i]);
                if (i + 1 < fields.length) {
                    items.push(fields[i + 1]);
                }
                fieldGroup.push(items);
            }
            return fieldGroup;
        },
        code() {
            const code = this.$uuid.v4().split("-").shift().toUpperCase();
            return `${code.slice(0, 4)}-${code.slice(-4)}`;
        },
    },
};
</script>

<style scoped>
</style>